import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api, { formatApiErrorDetail } from '@/api/axios';
import { UserPlus, UserGear, ChatCircleDots, Buildings, Trash, Power, Eye, EyeSlash, PencilSimple, MapPin, ShieldSlash, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MetaDataDeletion } from '@/components/MetaDataDeletion';
import ConfigurarAcceso from '@/components/ConfigurarAcceso';

const ModuleVisibilityTab = ({ users }) => {
  const [modules, setModules] = useState([]);
  const [saving, setSaving] = useState('');

  const load = async () => {
    try {
      const res = await api.get('/admin/module-visibility');
      setModules(res.data.modules || []);
    } catch (e) { /* silencioso */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (mod, hidden, allowed) => {
    try {
      setSaving(mod.module);
      await api.put(`/admin/module-visibility/${mod.module}`, { hidden, allowed_user_ids: allowed });
      setModules((prev) => prev.map((m) => m.module === mod.module ? { ...m, hidden, allowed_user_ids: allowed } : m));
      toast.success(`${mod.label}: ${hidden ? 'oculto' : 'visible para todos'}`);
    } catch (e) {
      toast.error('Error guardando visibilidad');
    } finally { setSaving(''); }
  };

  const toggleUser = (mod, userId) => {
    const allowed = mod.allowed_user_ids.includes(userId)
      ? mod.allowed_user_ids.filter((id) => id !== userId)
      : [...mod.allowed_user_ids, userId];
    save(mod, mod.hidden, allowed);
  };

  const activeUsers = (users || []).filter((u) => u.active !== false);

  return (
    <div className="space-y-4" data-testid="module-visibility-tab">
      <p className="text-sm text-[var(--text-muted)]">
        Módulos ocultos del menú para todos. Marca "Visible para todos" para reactivarlo, o asigna usuarios específicos que sí pueden verlo aunque esté oculto.
      </p>
      {modules.map((mod) => (
        <div key={mod.module} className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] p-4" data-testid={`module-vis-${mod.module}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {mod.hidden ? <EyeSlash size={20} className="text-red-500" /> : <Eye size={20} className="text-emerald-500" />}
              <span className="font-bold text-[var(--text)]">{mod.label}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${mod.hidden ? 'bg-red-500/10 text-red-500' : 'bg-emerald-500/10 text-emerald-600'}`}>
                {mod.hidden ? 'OCULTO' : 'VISIBLE'}
              </span>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!mod.hidden}
                disabled={saving === mod.module}
                onChange={(e) => save(mod, !e.target.checked, mod.allowed_user_ids)}
                data-testid={`module-vis-toggle-${mod.module}`}
                className="w-4 h-4"
              />
              Visible para todos
            </label>
          </div>
          {mod.hidden && (
            <div className="mt-3 pt-3 border-t border-[var(--border)]">
              <p className="text-xs font-bold text-[var(--text-muted)] mb-2">Excepciones — estos usuarios SÍ lo pueden ver:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                {activeUsers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                    <input
                      type="checkbox"
                      checked={mod.allowed_user_ids.includes(u.id)}
                      disabled={saving === mod.module}
                      onChange={() => toggleUser(mod, u.id)}
                      className="w-3.5 h-3.5"
                    />
                    {u.name} <span className="text-[var(--text-muted)]">({u.role})</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Admin = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordEditUser, setPasswordEditUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editName, setEditName] = useState('');
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [kardexFilterUser, setKardexFilterUser] = useState('');
  const [kardexFilterAction, setKardexFilterAction] = useState('');
  const [kardexFilterDateFrom, setKardexFilterDateFrom] = useState('');
  const [kardexFilterDateTo, setKardexFilterDateTo] = useState('');
  // Module denylist modal state
  const [isDenyDialogOpen, setIsDenyDialogOpen] = useState(false);
  const [denyTargetUser, setDenyTargetUser] = useState(null);
  const [denyDraft, setDenyDraft] = useState([]);
  const DENYABLE_MODULES = [
    { id: 'cotizador',    label: 'Cotizador',     desc: 'Crear/ver cotizaciones' },
    { id: 'pendientes',   label: 'Pendientes',    desc: 'Notas y tareas personales / equipo' },
    { id: 'chat_interno', label: 'Chat interno',  desc: 'Mensajería entre usuarios' },
    { id: 'instaladores', label: 'Instaladores',  desc: 'Gestión de instaladores (por defecto solo producción/supervisión)' },
  ];
  const [userForm, setUserForm] = useState({ name: '', email: '', username: '', password: '', role: 'user', business_ids: [] });
  const [permissionForm, setPermissionForm] = useState({
    business_id: '',
    permissions: [],
    tab_permissions: {},
  });
  const [isDeptDialogOpen, setIsDeptDialogOpen] = useState(false);
  const [deptUser, setDeptUser] = useState(null);
  const [deptList, setDeptList] = useState({ guatemala: [], special: [] });
  const [selectedDepts, setSelectedDepts] = useState([]);
  // Role presets (custom + system)
  const [roles, setRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ key: '', label: '', description: '', permissions: [], tab_permissions: {} });
  const [savingRole, setSavingRole] = useState(false);

  useEffect(() => {
    api.get('/departments').then(r => setDeptList(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/mi-dia');
      return;
    }
    fetchUsers();
    fetchBusinesses();
    fetchConversations();
    fetchRoles();
  }, [isAdmin, navigate]);

  const fetchRoles = async () => {
    try {
      const r = await api.get('/admin/roles');
      setRoles(r.data || []);
    } catch (e) {
      console.error('Error fetching roles', e);
      setRoles([]);
    } finally {
      setRolesLoaded(true);
    }
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm({ key: '', label: '', description: '', permissions: [], tab_permissions: {} });
    setIsRoleDialogOpen(true);
  };

  const openEditRole = (role) => {
    if (!role.editable) {
      toast.error('Este rol del sistema no se puede editar');
      return;
    }
    setEditingRole(role);
    setRoleForm({
      key: role.key,
      label: role.label,
      description: role.description || '',
      permissions: [...(role.permissions || [])],
      tab_permissions: role.tab_permissions ? { ...role.tab_permissions } : {},
    });
    setIsRoleDialogOpen(true);
  };

  const toggleRolePermission = (perm) => {
    setRoleForm((prev) => {
      const adding = !prev.permissions.includes(perm);
      const next = adding ? [...prev.permissions, perm] : prev.permissions.filter((p) => p !== perm);
      const tabPerms = { ...(prev.tab_permissions || {}) };
      if (!adding) delete tabPerms[perm];
      return { ...prev, permissions: next, tab_permissions: tabPerms };
    });
  };

  const handleSaveRole = async () => {
    if (!roleForm.label.trim()) {
      toast.error('El nombre del rol es obligatorio');
      return;
    }
    if (!editingRole && !roleForm.key.trim()) {
      toast.error('La clave del rol es obligatoria');
      return;
    }
    setSavingRole(true);
    try {
      if (editingRole) {
        await api.put(`/admin/roles/${editingRole.id}`, {
          label: roleForm.label.trim(),
          description: roleForm.description.trim(),
          permissions: roleForm.permissions,
          tab_permissions: roleForm.tab_permissions,
        });
        toast.success('Rol actualizado');
      } else {
        await api.post('/admin/roles', {
          key: roleForm.key.trim().toLowerCase().replace(/\s+/g, '_'),
          label: roleForm.label.trim(),
          description: roleForm.description.trim(),
          permissions: roleForm.permissions,
          tab_permissions: roleForm.tab_permissions,
        });
        toast.success('Rol creado');
      }
      setIsRoleDialogOpen(false);
      fetchRoles();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Error');
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`¿Eliminar el rol "${role.label}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/roles/${role.id}`);
      toast.success('Rol eliminado');
      fetchRoles();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Error');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await api.get('/businesses');
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchActivityLog = async () => {
    setActivityLoading(true);
    try {
      const response = await api.get('/admin/activity-log');
      setActivityLog(response.data);
    } catch (error) {
      console.error('Error fetching activity log:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const [paymentAudit, setPaymentAudit] = useState([]);
  const [paymentAuditLoading, setPaymentAuditLoading] = useState(false);
  const fetchPaymentAudit = async () => {
    setPaymentAuditLoading(true);
    try {
      const response = await api.get('/produccion/payment-audit', { params: { limit: 200 } });
      setPaymentAudit(response.data.items || []);
    } catch (error) {
      console.error('Error fetching payment audit:', error);
      toast.error('Error cargando bitácora');
    } finally {
      setPaymentAuditLoading(false);
    }
  };

  const [creatingUser, setCreatingUser] = useState(false);
  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (creatingUser) return;
    setCreatingUser(true);
    try {
      const autoUsername = userForm.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') + '_' + Date.now().toString(36);
      const payload = {
        name: userForm.name,
        password: userForm.password,
        username: autoUsername,
        role: userForm.role || 'user',
        business_ids: userForm.business_ids || [],
      };
      await api.post('/admin/users', payload);
      toast.success('Usuario creado con permisos del rol aplicados');
      setIsUserDialogOpen(false);
      setUserForm({ name: '', email: '', username: '', password: '', role: 'user', business_ids: [] });
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const password = window.prompt(`Para eliminar a "${userName}", escribe la contrasena de seguridad:`);
    if (!password) return;
    if (password !== 'estrellita') {
      toast.error('Contrasena de seguridad incorrecta');
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const res = await api.put(`/admin/users/${userId}/toggle-active`);
      toast.success(res.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleUnlockUser = async (user) => {
    if (!window.confirm(`¿Desbloquear la cuenta de "${user.name}"?\n\nEsto limpia los intentos fallidos de login y reactiva la cuenta.`)) return;
    try {
      const { data: diag } = await api.get(`/admin/users/${user.id}/diagnose-login`);
      const res = await api.post(`/admin/users/${user.id}/unlock`);
      const intentos = diag.failed_attempts?.length || 0;
      const bloqueado = diag.is_locked ? ' (ESTABA BLOQUEADO)' : '';
      toast.success(`✓ Cuenta desbloqueada${bloqueado} · ${res.data.intentos_borrados} intento(s) borrado(s) · Estado: ${diag.active ? 'Activo' : 'Inactivo'} → Activo`);
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Error al desbloquear');
    }
  };

  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordEditUser) return;
    let updated = false;
    // Update name if changed
    if (editName && editName !== passwordEditUser.name) {
      try {
        await api.put(`/admin/users/${passwordEditUser.id}/name`, { name: editName });
        toast.success('Nombre actualizado');
        updated = true;
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail));
        return;
      }
    }
    // Update password if provided
    if (newPassword) {
      try {
        await api.put(`/admin/users/${passwordEditUser.id}/password`, { password: newPassword });
        toast.success('Contrasena actualizada');
        updated = true;
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail));
        return;
      }
    }
    if (updated) {
      setIsPasswordDialogOpen(false);
      setPasswordEditUser(null);
      setNewPassword('');
      setEditName('');
      fetchUsers();
    } else {
      toast.info('No se realizaron cambios');
    }
  };

  const handleUpdatePermissions = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedUser || !permissionForm.business_id) {
      toast.error('Selecciona un negocio');
      return;
    }

    try {
      await api.put(`/admin/users/${selectedUser.id}/permissions`, {
        user_id: selectedUser.id,
        ...permissionForm
      });
      toast.success('Permisos actualizados');
      setIsPermissionDialogOpen(false);
      setPermissionForm({ business_id: '', permissions: [], tab_permissions: {} });
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar permisos');
    }
  };

  const openDenyModal = (user) => {
    setDenyTargetUser(user);
    setDenyDraft([...(user.denied_modules || [])]);
    setIsDenyDialogOpen(true);
  };

  const handleSaveDenyModules = async () => {
    if (!denyTargetUser) return;
    try {
      await api.put(`/admin/users/${denyTargetUser.id}/denied-modules`, {
        denied_modules: denyDraft,
      });
      toast.success(`Bloqueos actualizados para ${denyTargetUser.name}`);
      setIsDenyDialogOpen(false);
      setDenyTargetUser(null);
      setDenyDraft([]);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar bloqueos');
    }
  };

  const handleAssignConversation = async (userId) => {
    if (!selectedConversation) return;

    try {
      await api.put(`/admin/conversations/${selectedConversation.id}/assign`, {
        assigned_to: userId || null,
      });      toast.success('Conversación asignada');
      setIsAssignDialogOpen(false);
      setSelectedConversation(null);
      fetchConversations();
    } catch (error) {
      toast.error('Error al asignar conversación');
    }
  };

  const togglePermission = (perm) => {
    setPermissionForm((prev) => {
      const adding = !prev.permissions.includes(perm);
      const perms = adding
        ? [...prev.permissions, perm]
        : prev.permissions.filter((p) => p !== perm);
      const tabPerms = { ...(prev.tab_permissions || {}) };
      if (adding && MODULE_TABS[perm]) {
        // Auto-mark ALL tabs by default when enabling a module with sub-tabs
        tabPerms[perm] = MODULE_TABS[perm].map((t) => t.key);
      } else if (!adding && tabPerms[perm]) {
        delete tabPerms[perm];
      }
      return { ...prev, permissions: perms, tab_permissions: tabPerms };
    });
  };

  // Sub-permissions (tabs) per module — extendable in the future
  const MODULE_TABS = {
    vendors: [
      { key: 'basico', label: 'Básicos' },
      { key: 'laboral', label: 'Laborales' },
      { key: 'deptos', label: 'Departamentos' },
      { key: 'emergencia', label: 'Emergencia' },
      { key: 'metricas', label: 'Métricas' },
      { key: 'plan', label: 'Plan / Metas CYH-SA' },
      { key: 'notas', label: 'Notas' },
      { key: 'permisos', label: 'Permisos (admin)' },
    ],
    produccion: [
      { key: 'datos', label: 'Datos OT' },
      { key: 'pagos', label: 'Pagos / Anticipos' },
      { key: 'materiales', label: 'Materiales' },
      { key: 'mano_obra', label: 'Mano de obra' },
      { key: 'gastos', label: 'Gastos operativos' },
      { key: 'documentos', label: 'Documentos / Fotos' },
      { key: 'timeline', label: 'Timeline / Bitácora' },
      { key: 'rentabilidad', label: 'Rentabilidad (sensible)' },
    ],
    harperp: [
      { key: 'ventas', label: 'Ventas' },
      { key: 'vendedores', label: 'Por Vendedor' },
      { key: 'inventario', label: 'Inventario' },
      { key: 'costos', label: 'Costos y Márgenes (sensible)' },
    ],
  };

  // Convention: when tab_permissions[module] is undefined/null → ALL tabs are implicitly granted.
  // When it's an empty array [] → user explicitly granted NO tabs.
  const getEffectiveTabPermissions = (module) => {
    const tp = permissionForm.tab_permissions?.[module];
    if (tp === undefined || tp === null) {
      return MODULE_TABS[module] ? MODULE_TABS[module].map((t) => t.key) : [];
    }
    return tp;
  };

  const toggleTabPermission = (module, tab) => {
    setPermissionForm((prev) => {
      const stored = prev.tab_permissions?.[module];
      // If stored is undefined → user is editing for the first time. Start from ALL tabs.
      const current = (stored === undefined || stored === null)
        ? (MODULE_TABS[module] ? MODULE_TABS[module].map((t) => t.key) : [])
        : stored;
      const next = current.includes(tab)
        ? current.filter((t) => t !== tab)
        : [...current, tab];
      return {
        ...prev,
        tab_permissions: { ...(prev.tab_permissions || {}), [module]: next },
      };
    });
  };

  const setAllTabPermissions = (module, allChecked) => {
    setPermissionForm((prev) => ({
      ...prev,
      tab_permissions: {
        ...(prev.tab_permissions || {}),
        [module]: allChecked ? MODULE_TABS[module].map((t) => t.key) : [],
      },
    }));
  };

  const openDeptDialog = (user) => {
    setDeptUser(user);
    setSelectedDepts(user.departments || []);
    setIsDeptDialogOpen(true);
  };

  const toggleDept = (dept) => {
    setSelectedDepts((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]
    );
  };

  const handleSaveDepartments = async () => {
    if (!deptUser) return;
    try {
      await api.put(`/admin/users/${deptUser.id}/departments`, { departments: selectedDepts });
      toast.success('Departamentos actualizados');
      setIsDeptDialogOpen(false);
      setDeptUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error) || 'Error al guardar departamentos');
    }
  };

  const availablePermissions = [
    'dashboard',
    'mi_dia',
    'inbox',
    'inbox_asignado',
    'customers',
    'orders',
    'catalogo',
    'templates',
    'reports',
    'reportes',
    'prospectos',
    'ai_costos',
    'ranking',
    'vendors',
    'coverage',
    'plan_trabajo',
    'clientes_vendedores',
    'clientes_redes',
    'cotizador',
    'presupuestos_sa',
    'produccion',
    'programacion',
    'pendientes',
    'chat_interno',
    'alertas',
    'wa_campaigns',
    'asistente_ia',
    'chat_ia',
    'harperp',
    'dashboard_gerencial',
    'dashboard_comercial',
    'settings',
    'landing_pages',
    'leads_landing',
    'tienda_admin',
  ];

  const permissionLabels = {
    dashboard: 'Dashboard',
    mi_dia: 'Mi Día (vendedor)',
    inbox: 'Mensajes (todos)',
    inbox_asignado: 'Mensajes (solo asignados)',
    customers: 'Clientes',
    orders: 'Pedidos',
    catalogo: 'Catálogo',
    templates: 'Plantillas',
    reports: 'Reportes (clásico)',
    reportes: 'Reportes (semanal CYH/SA)',
    prospectos: 'Prospectos IA',
    ai_costos: 'Costos IA',
    ranking: 'Ranking de Vendedores',
    vendors: 'Vendedores',
    coverage: 'Cobertura territorial',
    plan_trabajo: 'Plan de Trabajo',
    clientes_vendedores: 'Directorio - Clientes Vendedores',
    clientes_redes: 'Directorio - Clientes Redes Sociales',
    cotizador: 'Cotizador (CRM ↔ HarpERP)',
    presupuestos_sa: 'Presupuestos SA (Cotizador técnico)',
    produccion: 'Producción (Órdenes de Trabajo)',
    pendientes: 'Pendientes (Notas / To-Do)',
    chat_interno: 'Chat Interno (entre usuarios)',
    alertas: 'Alertas IA (predictivas)',
    wa_campaigns: 'Campañas WhatsApp',
    asistente_ia: 'Asistente IA (chat)',
    chat_ia: 'Chat IA (asistente conversacional con datos)',
    harperp: 'HarpERP (ERP: ventas, inventario, costos)',
    dashboard_gerencial: 'Dashboard Gerencial (privado)',
    dashboard_comercial: 'Dashboard Comercial (ventas, sin costos)',
    settings: 'Configuración',
    landing_pages: 'Landing Pages (Soluciones y productos)',
    leads_landing: 'Leads Landing (seguimiento de leads de landing pages)',
    tienda_admin: 'Tienda en Línea (admin)',
  };

  // Quick presets per role to onboard faster
  const ROLE_PRESETS = {
    user: ['inbox_asignado', 'customers', 'orders', 'catalogo', 'mi_dia', 'cotizador', 'pendientes', 'chat_interno'],
    operaciones: ['mi_dia', 'produccion', 'cotizador', 'pendientes', 'chat_interno'],
    supervisor: ['dashboard', 'inbox', 'customers', 'orders', 'catalogo', 'templates', 'reports', 'reportes', 'ranking', 'vendors', 'coverage', 'plan_trabajo', 'prospectos', 'mi_dia', 'clientes_vendedores', 'clientes_redes', 'cotizador', 'produccion', 'pendientes', 'chat_interno'],
    contable: ['dashboard', 'orders', 'reports', 'reportes', 'ai_costos', 'customers', 'cotizador', 'pendientes', 'chat_interno'],
    gerente: ['dashboard', 'inbox', 'customers', 'orders', 'catalogo', 'templates', 'reports', 'reportes', 'ranking', 'vendors', 'coverage', 'plan_trabajo', 'prospectos', 'ai_costos', 'mi_dia', 'clientes_vendedores', 'clientes_redes', 'cotizador', 'produccion', 'pendientes', 'chat_interno'],
  };

  const applyRolePreset = (roleKey) => {
    // Prefer dynamic role from DB (includes custom roles), fallback to hardcoded preset
    const dbRole = roles.find(r => r.key === roleKey);
    const preset = dbRole?.permissions || ROLE_PRESETS[roleKey] || [];
    const dbTabPerms = dbRole?.tab_permissions || {};
    // Auto-mark all tabs for modules with sub-tabs that are in the preset (unless DB role already specifies)
    const tabPerms = { ...dbTabPerms };
    preset.forEach((module) => {
      if (MODULE_TABS[module] && tabPerms[module] === undefined) {
        tabPerms[module] = MODULE_TABS[module].map((t) => t.key);
      }
    });
    setPermissionForm((prev) => ({ ...prev, permissions: preset, tab_permissions: tabPerms }));
    toast.success(`Plantilla "${dbRole?.label || roleKey}" aplicada (${preset.length} módulos)`);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-full p-6 pb-24" data-testid="admin-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-4xl tracking-tight font-black text-[var(--text)] mb-2">
          Administración
        </h1>
        <p className="text-sm text-[var(--text-muted)]">Gestiona usuarios, permisos y asignaciones</p>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="w-full overflow-x-auto justify-start flex-nowrap whitespace-nowrap scroll-touch-x">
          <TabsTrigger value="users">Usuarios</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles</TabsTrigger>
          <TabsTrigger value="permissions">Permisos</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules">Módulos</TabsTrigger>
          <TabsTrigger value="conversations">Asignar Conversaciones</TabsTrigger>
          <TabsTrigger value="kardex" onClick={() => { if (activityLog.length === 0) fetchActivityLog(); }}>Kardex</TabsTrigger>
          <TabsTrigger value="payment_audit" data-testid="tab-payment-audit" onClick={() => { if (paymentAudit.length === 0) fetchPaymentAudit(); }}>Bitácora PINs</TabsTrigger>
          <TabsTrigger value="meta_deletion" data-testid="tab-meta-deletion">Datos Meta</TabsTrigger>
        </TabsList>

        {/* USERS TAB */}
        <TabsContent value="users" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">
              Gestión de Usuarios
            </h2>
            <Button
              onClick={() => setIsUserDialogOpen(true)}
              className="bg-[#1D4ED8] hover:bg-[#1E40AF]"
              data-testid="create-user-btn"
            >
              <UserPlus size={18} weight="bold" className="mr-2" />
              Crear Usuario
            </Button>
          </div>

          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] scroll-touch-x">
            <table className="w-full min-w-[900px]" data-testid="users-table">
              <thead className="bg-[var(--bg)] border-b border-[var(--border)]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Nombre
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Contrasena
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Rol
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Fecha Creacion
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Empresas y Modulos
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Departamentos
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-[var(--border)] hover:bg-[var(--bg)] transition-colors duration-150"
                    data-testid={`user-row-${user.id}`}
                  >
                    <td className="px-4 py-3 text-sm text-[var(--text)] font-bold">{user.name}</td>
                    <td className="px-4 py-3 text-sm text-[var(--text-muted)]">{user.email || user.username || '-'}</td>
                    <td className="px-4 py-3">
                      {user.role !== 'admin' ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-[var(--text)] font-mono" data-testid={`user-password-${user.id}`}>
                            {visiblePasswords[user.id] ? (user.plain_password || '—') : '••••••'}
                          </span>
                          <button
                            onClick={() => togglePasswordVisibility(user.id)}
                            className="p-1 text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
                            data-testid={`toggle-password-visibility-${user.id}`}
                          >
                            {visiblePasswords[user.id] ? <EyeSlash size={14} /> : <Eye size={14} />}
                          </button>
                          <button
                            onClick={() => {
                              setPasswordEditUser(user);
                              setEditName(user.name);
                              setNewPassword('');
                              setIsPasswordDialogOpen(true);
                            }}
                            className="p-1 text-[var(--text-muted)] hover:text-[#1D4ED8] transition-colors"
                            data-testid={`edit-password-${user.id}`}
                          >
                            <PencilSimple size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--text-muted)]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-1 rounded-[12px] font-bold ${
                          user.role === 'admin'
                            ? 'bg-[#1D4ED8] bg-opacity-10 text-[#1D4ED8]'
                            : user.role === 'supervisor'
                            ? 'bg-[#7C3AED] bg-opacity-10 text-[#7C3AED]'
                            : 'bg-[var(--bg)] border border-[var(--border)] text-white'
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
                      {new Date(user.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-4 py-3 max-w-[260px]">
                      {user.role === 'admin' ? (
                        <span className="text-xs text-[#1D4ED8] font-black uppercase">Administrador — Acceso total</span>
                      ) : user.assigned_permissions?.length > 0 ? (
                        <div className="space-y-1">
                          {user.assigned_permissions.map((ap, idx) => (
                            <div key={idx} className="text-xs" title={ap.permissions.map((p) => permissionLabels[p] || p).join(', ')}>
                              <div className="flex items-center gap-1.5">
                                <span className="font-bold text-[var(--text)] truncate max-w-[160px]">{ap.business_name}</span>
                                <span className="px-1.5 py-0.5 bg-[#1D4ED8]/10 text-[#1D4ED8] border border-[#1D4ED8]/20 rounded-full text-[10px] font-bold shrink-0">
                                  {ap.permissions.length} mód.
                                </span>
                              </div>
                              <p className="text-[10px] text-[var(--text-muted)] truncate max-w-[240px]">
                                {ap.permissions.slice(0, 3).map((p) => permissionLabels[p] || p).join(' · ')}{ap.permissions.length > 3 ? ` +${ap.permissions.length - 3}` : ''}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-[#DC2626]">Sin permisos asignados</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.role === 'admin' ? (
                        <span className="text-xs text-[#1D4ED8] font-bold">N/A</span>
                      ) : (user.departments?.length > 0) ? (
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {user.departments.slice(0, 4).map(d => (
                            <span key={d} className="text-[10px] px-1.5 py-0.5 bg-[#1D4ED8]/10 text-[#1D4ED8] border border-[#1D4ED8]/30 rounded font-bold">
                              {d}
                            </span>
                          ))}
                          {user.departments.length > 4 && (
                            <span className="text-[10px] text-[var(--text-muted)] font-bold">+{user.departments.length - 4}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--text-faint)]">Ninguno</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-[12px] font-bold ${
                        user.active === false
                          ? 'bg-red-50 text-red-600'
                          : 'bg-emerald-50 text-emerald-600'
                      }`} data-testid={`user-status-${user.id}`}>
                        {user.active === false ? 'Inactivo' : 'Activo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {user.role !== 'admin' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setAccessUser(user)}
                              className="border-[#1D4ED8]/50 text-[#1D4ED8] hover:bg-[#1D4ED8]/10 font-bold"
                              data-testid={`configurar-acceso-${user.id}`}
                            >
                              <UserGear size={16} weight="bold" className="mr-1" />
                              Configurar acceso
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                const firstPerm = user.assigned_permissions?.[0];
                                if (firstPerm) {
                                  setPermissionForm({
                                    business_id: firstPerm.business_id,
                                    permissions: [...firstPerm.permissions],
                                    tab_permissions: firstPerm.tab_permissions ? { ...firstPerm.tab_permissions } : {},
                                  });
                                } else {
                                  setPermissionForm({ business_id: '', permissions: [], tab_permissions: {} });
                                }
                                setIsPermissionDialogOpen(true);
                              }}
                              data-testid={`manage-permissions-${user.id}`}
                              title="Editor clásico de permisos (avanzado)"
                            >
                              <UserGear size={16} weight="bold" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeptDialog(user)}
                              data-testid={`manage-departments-${user.id}`}
                              title="Departamentos"
                            >
                              <MapPin size={16} weight="bold" className="mr-1" />
                              Deptos
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDenyModal(user)}
                              data-testid={`manage-denied-${user.id}`}
                              title="Módulos bloqueados"
                              className={(user.denied_modules?.length > 0) ? 'border-red-300 text-red-600 hover:bg-red-50' : ''}
                            >
                              <ShieldSlash size={16} weight="bold" className="mr-1" />
                              Bloqueos
                              {user.denied_modules?.length > 0 && (
                                <span className="ml-1 text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5">{user.denied_modules.length}</span>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleActive(user.id)}
                              className={user.active === false ? 'border-emerald-300 text-emerald-600 hover:bg-emerald-50' : 'border-amber-300 text-amber-600 hover:bg-amber-50'}
                              data-testid={`toggle-active-${user.id}`}
                            >
                              <Power size={16} weight="bold" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnlockUser(user)}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              data-testid={`unlock-user-${user.id}`}
                              title="Desbloquear cuenta (limpia intentos fallidos y reactiva)"
                            >
                              <ShieldCheck size={16} weight="bold" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id, user.name)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash size={16} weight="bold" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ROLES TAB */}
        <TabsContent value="roles" className="mt-6">
          <div className="bg-[var(--surface)] border border-[var(--border)] p-4 sm:p-6 rounded-[12px]" data-testid="roles-panel">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">Roles & Plantillas de Permisos</h2>
                <p className="text-sm text-[var(--text-muted)] mt-0.5">
                  Define qué módulos tendrá cada rol por defecto. Al crear un usuario, los permisos del rol se aplican automáticamente a los negocios seleccionados.
                </p>
              </div>
              <Button onClick={openCreateRole} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold" data-testid="btn-create-role">
                + Nuevo Rol
              </Button>
            </div>
            {!rolesLoaded ? (
              <p className="text-sm text-[var(--text-muted)] py-8 text-center">Cargando roles…</p>
            ) : roles.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] py-8 text-center">Sin roles. Crea el primero con &quot;+ Nuevo Rol&quot;.</p>
            ) : (
              <div className="space-y-2">
                {roles.map(role => (
                  <div key={role.id} className="flex items-center justify-between gap-3 p-3 bg-[var(--bg)] border border-[var(--border)] rounded-[10px] flex-wrap" data-testid={`role-row-${role.key}`}>
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-[var(--text)]">{role.label}</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-[var(--surface-muted)] rounded font-mono text-[var(--text-muted)]">{role.key}</span>
                        {role.is_system && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/10 text-blue-600 dark:text-blue-300 rounded font-bold">SISTEMA</span>
                        )}
                        {!role.editable && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-red-500/10 text-red-600 dark:text-red-300 rounded font-bold">BLOQUEADO</span>
                        )}
                      </div>
                      {role.description && <p className="text-xs text-[var(--text-muted)] mt-1">{role.description}</p>}
                      <p className="text-[11px] text-[var(--text-muted)] mt-1">
                        <span className="font-bold text-[#1D4ED8]">{(role.permissions || []).length}</span> módulos asignados
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditRole(role)}
                        disabled={!role.editable}
                        data-testid={`btn-edit-role-${role.key}`}
                      >
                        {role.editable ? 'Editar' : 'Bloqueado'}
                      </Button>
                      {!role.is_system && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteRole(role)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          data-testid={`btn-delete-role-${role.key}`}
                        >
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* PERMISSIONS TAB */}
        <TabsContent value="permissions" className="mt-6">
          <div className="bg-[var(--surface)] border border-[var(--border)] p-6 rounded-[12px]">
            <h2 className="text-lg tracking-tight font-bold text-[var(--text)] mb-4">
              Resumen de Permisos
            </h2>
            <p className="text-sm text-[var(--text-muted)] mb-4">
              Usa la pestaña de Usuarios para gestionar permisos individuales
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {users
                .filter((u) => u.role !== 'admin')
                .map((user) => (
                  <div
                    key={user.id}
                    className="p-4 border border-[var(--border)] rounded-[12px]"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-[var(--text)]">{user.name}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(user);
                          const firstPerm = user.assigned_permissions?.[0];
                          if (firstPerm) {
                            setPermissionForm({
                              business_id: firstPerm.business_id,
                              permissions: [...firstPerm.permissions],
                              tab_permissions: firstPerm.tab_permissions ? { ...firstPerm.tab_permissions } : {},
                            });
                          } else {
                            setPermissionForm({ business_id: '', permissions: [], tab_permissions: {} });
                          }
                          setIsPermissionDialogOpen(true);
                        }}
                      >
                        Editar
                      </Button>
                    </div>
                    <p className="text-xs text-[var(--text-muted)]">{user.email || user.username || '-'}</p>
                  </div>
                ))}
            </div>
          </div>
        </TabsContent>

        {/* CONVERSATIONS TAB */}
        <TabsContent value="modules" className="mt-6">
          <ModuleVisibilityTab users={users} />
        </TabsContent>

        <TabsContent value="conversations" className="mt-6">
          <div className="mb-4">
            <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">
              Asignar Conversaciones
            </h2>
            <p className="text-sm text-[var(--text-muted)]">
              Asigna conversaciones a usuarios específicos
            </p>
          </div>

          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] scroll-touch-x">
            <table className="w-full min-w-[800px]" data-testid="conversations-table">
              <thead className="bg-[var(--bg)] border-b border-[var(--border)]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Plataforma
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Asignado a
                  </th>
                  <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((conv) => {
                  const assignedUser = users.find((u) => u.id === conv.assigned_to);
                  return (
                    <tr
                      key={conv.id}
                      className="border-b border-[var(--border)] hover:bg-[var(--bg)] transition-colors duration-150"
                    >
                      <td className="px-4 py-3 text-sm text-[var(--text)] font-bold">
                        {conv.customer_id.substring(0, 8)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-xs px-2 py-1 rounded-[12px] font-bold ${
                            conv.platform === 'whatsapp'
                              ? 'bg-[#25D366] bg-opacity-10 text-[#25D366]'
                              : 'bg-[#0084FF] bg-opacity-10 text-[#0084FF]'
                          }`}
                        >
                          {conv.platform}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 bg-[var(--bg)] border border-[var(--border)] rounded-[12px]">
                          {conv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
                        {assignedUser ? assignedUser.name : 'Sin asignar'}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedConversation(conv);
                            setIsAssignDialogOpen(true);
                          }}
                          data-testid={`assign-conversation-${conv.id}`}
                        >
                          <ChatCircleDots size={16} weight="bold" className="mr-1" />
                          Asignar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* KARDEX TAB */}
        <TabsContent value="kardex" className="mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">
                Kardex de Actividades
              </h2>
              <p className="text-sm text-[var(--text-muted)]">Registro de movimientos de usuarios en el CRM</p>
            </div>
            <Button variant="outline" onClick={fetchActivityLog} data-testid="refresh-kardex-btn">
              Actualizar
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <select
              value={kardexFilterUser}
              onChange={(e) => setKardexFilterUser(e.target.value)}
              className="text-sm p-2 border border-[var(--border)] rounded-[12px] focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none"
              data-testid="kardex-filter-user"
            >
              <option value="">Todos los usuarios</option>
              {[...new Set(activityLog.map(a => a.user_name))].sort().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <select
              value={kardexFilterAction}
              onChange={(e) => setKardexFilterAction(e.target.value)}
              className="text-sm p-2 border border-[var(--border)] rounded-[12px] focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none"
              data-testid="kardex-filter-action"
            >
              <option value="">Todos los movimientos</option>
              <option value="login">Inicio de sesion</option>
              <option value="logout">Cerro sesion</option>
              <option value="mensaje_enviado">Mensaje enviado</option>
              <option value="pedido_creado">Pedido creado</option>
              <option value="producto_creado">Producto creado</option>
              <option value="producto_editado">Producto editado</option>
              <option value="producto_eliminado">Producto eliminado</option>
              <option value="conversacion_asignada">Conversacion asignada</option>
              <option value="usuario_creado">Usuario creado</option>
              <option value="permisos_actualizados">Permisos actualizados</option>
            </select>
            <Input
              type="date"
              value={kardexFilterDateFrom}
              onChange={(e) => setKardexFilterDateFrom(e.target.value)}
              className="w-auto text-sm"
              placeholder="Desde"
              data-testid="kardex-filter-date-from"
            />
            <Input
              type="date"
              value={kardexFilterDateTo}
              onChange={(e) => setKardexFilterDateTo(e.target.value)}
              className="w-auto text-sm"
              placeholder="Hasta"
              data-testid="kardex-filter-date-to"
            />
            {(kardexFilterUser || kardexFilterAction || kardexFilterDateFrom || kardexFilterDateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setKardexFilterUser(''); setKardexFilterAction(''); setKardexFilterDateFrom(''); setKardexFilterDateTo(''); }}
                className="text-[#DC2626] hover:text-[#DC2626]"
                data-testid="kardex-clear-filters"
              >
                Limpiar filtros
              </Button>
            )}
          </div>

          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] scroll-touch-x">
            {activityLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#1D4ED8] border-t-transparent" />
              </div>
            ) : (() => {
              const actionLabels = {
                login: 'Inicio de sesion',
                logout: 'Cerro sesion',
                mensaje_enviado: 'Mensaje enviado',
                pedido_creado: 'Pedido creado',
                pedido_editado: 'Pedido editado',
                producto_creado: 'Producto creado',
                producto_editado: 'Producto editado',
                producto_eliminado: 'Producto eliminado',
                conversacion_asignada: 'Conversacion asignada',
                usuario_creado: 'Usuario creado',
                usuario_desactivado: 'Usuario desactivado',
                usuario_eliminado: 'Usuario eliminado',
                permisos_actualizados: 'Permisos actualizados',
              };
              const actionColors = {
                login: 'bg-emerald-50 text-emerald-700',
                logout: 'bg-gray-100 text-gray-600',
                mensaje_enviado: 'bg-blue-50 text-blue-700',
                pedido_creado: 'bg-amber-50 text-amber-700',
                pedido_editado: 'bg-amber-50 text-amber-700',
                producto_creado: 'bg-pink-50 text-pink-700',
                producto_editado: 'bg-pink-50 text-pink-700',
                producto_eliminado: 'bg-red-50 text-red-700',
                conversacion_asignada: 'bg-purple-50 text-purple-700',
                usuario_creado: 'bg-indigo-50 text-indigo-700',
                usuario_desactivado: 'bg-orange-50 text-orange-700',
                usuario_eliminado: 'bg-red-50 text-red-700',
                permisos_actualizados: 'bg-indigo-50 text-indigo-700',
              };
              const filtered = activityLog.filter(act => {
                if (kardexFilterUser && act.user_name !== kardexFilterUser) return false;
                if (kardexFilterAction && act.action !== kardexFilterAction) return false;
                if (kardexFilterDateFrom) {
                  const actDate = act.timestamp.slice(0, 10);
                  if (actDate < kardexFilterDateFrom) return false;
                }
                if (kardexFilterDateTo) {
                  const actDate = act.timestamp.slice(0, 10);
                  if (actDate > kardexFilterDateTo) return false;
                }
                return true;
              });
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-12">
                    <p className="text-sm text-[var(--text-muted)]">
                      {activityLog.length === 0 ? 'Sin actividades registradas' : 'Sin resultados para los filtros seleccionados'}
                    </p>
                  </div>
                );
              }
              return (
                <>
                  <div className="px-4 py-2 bg-[var(--bg)] border-b border-[var(--border)] text-xs text-[var(--text-muted)]">
                    Mostrando {filtered.length} de {activityLog.length} actividades
                  </div>
                  <table className="w-full min-w-[700px]" data-testid="kardex-table">
                    <thead className="bg-[var(--bg)] border-b border-[var(--border)]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">Fecha/Hora</th>
                        <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">Usuario</th>
                        <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">Movimiento</th>
                        <th className="px-4 py-3 text-left text-xs tracking-[0.1em] uppercase font-bold text-[var(--text-muted)]">Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((act, idx) => (
                        <tr key={idx} className="border-b border-[var(--border)] hover:bg-[var(--bg)] transition-colors" data-testid={`kardex-row-${idx}`}>
                          <td className="px-4 py-2.5 text-xs text-[var(--text-muted)] whitespace-nowrap">
                            {new Date(act.timestamp).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'medium' })}
                          </td>
                          <td className="px-4 py-2.5 text-sm font-bold text-[var(--text)]">{act.user_name}</td>
                          <td className="px-4 py-2.5">
                            <span className={`text-xs px-2 py-0.5 rounded-[12px] font-bold ${actionColors[act.action] || 'bg-gray-100 text-gray-600'}`}>
                              {actionLabels[act.action] || act.action}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-[var(--text-muted)]">{act.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="payment_audit" className="mt-6">
          <div className="bg-[var(--surface)] border border-[var(--border)] p-4 sm:p-6 rounded-[12px]" data-testid="payment-audit-panel">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div>
                <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">🔐 Bitácora de PINs · Producción</h2>
                <p className="text-sm text-[var(--text-muted)] mt-0.5">
                  Cada uso del PIN <code>marena@</code> queda registrado aquí: anulaciones de OT, creación sin anticipo, eliminación de pagos.
                </p>
              </div>
              <Button variant="outline" onClick={fetchPaymentAudit} data-testid="refresh-payment-audit-btn">
                Recargar
              </Button>
            </div>
            {paymentAuditLoading ? (
              <p className="text-sm text-[var(--text-muted)] py-6 text-center">Cargando…</p>
            ) : paymentAudit.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] py-6 text-center italic">Sin registros aún. Cuando alguien use el PIN aparecerá aquí.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payment-audit-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] border-b border-[var(--border)]">
                      <th className="text-left py-2 pr-2">Fecha</th>
                      <th className="text-left py-2 px-2">Acción</th>
                      <th className="text-left py-2 px-2">Quien autorizó</th>
                      <th className="text-left py-2 px-2">OT</th>
                      <th className="text-left py-2 px-2">Motivo</th>
                      <th className="text-left py-2 pl-2">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentAudit.map((a) => (
                      <tr key={a.id} className="border-b border-[var(--border)]/40 hover:bg-[var(--surface-muted)]" data-testid={`audit-row-${a.id}`}>
                        <td className="py-2 pr-2 text-[var(--text-muted)] whitespace-nowrap">
                          {a.created_at ? new Date(a.created_at).toLocaleString('es-GT') : '—'}
                        </td>
                        <td className="py-2 px-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                            a.accion === 'anular_ot' ? 'bg-red-500/15 text-red-700 dark:text-red-300' :
                            a.accion === 'crear_ot_sin_anticipo' ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300' :
                            a.accion === 'eliminar_pago' ? 'bg-purple-500/15 text-purple-700 dark:text-purple-300' :
                            'bg-blue-500/15 text-blue-700 dark:text-blue-300'
                          }`}>
                            {a.accion}
                          </span>
                        </td>
                        <td className="py-2 px-2 font-bold text-[var(--text)]">{a.user_name || '—'}</td>
                        <td className="py-2 px-2 font-mono text-xs">{(a.extra?.numero) || (a.ot_id ? a.ot_id.slice(0, 8) + '…' : '—')}</td>
                        <td className="py-2 px-2 text-[var(--text)] max-w-xs truncate" title={a.motivo}>{a.motivo}</td>
                        <td className="py-2 pl-2 text-[10px] text-[var(--text-muted)]">
                          {a.extra?.monto ? `Q ${Number(a.extra.monto).toLocaleString('es-GT')}` : ''}
                          {a.extra?.tipo ? ` · ${a.extra.tipo}` : ''}
                          {a.extra?.estado_previo ? ` · estado: ${a.extra.estado_previo}` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-[var(--text-muted)] mt-2">{paymentAudit.length} registro(s)</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="meta_deletion" className="mt-6">
          <div className="bg-[var(--surface)] border border-[var(--border)] p-4 sm:p-6 rounded-[12px]">
            <MetaDataDeletion />
          </div>
        </TabsContent>
      </Tabs>

      {/* CREATE USER DIALOG */}
      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Usuario</DialogTitle>
            <DialogDescription>Completa los datos del nuevo usuario</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4" data-testid="create-user-form">
            <div>
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                required
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Contrasena</Label>
              <Input
                id="password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                required
                minLength={6}
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label htmlFor="role">Rol</Label>
              <select
                id="role"
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                className="w-full text-sm p-2 border border-[var(--border)] rounded-[12px] focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none"
                data-testid="user-role-select"
              >
                {roles.filter(r => r.key !== 'admin').map(r => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
                {roles.length === 0 && (
                  <>
                    <option value="user">Vendedor / Usuario</option>
                    <option value="operaciones">Operaciones (Producción)</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="contable">Contable</option>
                    <option value="gerente">Gerente</option>
                  </>
                )}
              </select>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                {(() => {
                  const sel = roles.find(r => r.key === userForm.role);
                  if (sel) return sel.description || `${(sel.permissions || []).length} permisos por defecto. Podrás ajustar después por negocio.`;
                  return 'Acceso básico según permisos asignados por negocio.';
                })()}
              </p>
            </div>
            <div>
              <Label>Negocios donde aplicar permisos del rol</Label>
              <div className="border border-[var(--border)] rounded-[12px] p-2 max-h-[150px] overflow-y-auto space-y-1">
                {businesses.length === 0 && <p className="text-xs text-[var(--text-muted)]">Sin negocios disponibles</p>}
                {businesses.map(biz => (
                  <label key={biz.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[var(--surface-muted)] px-2 py-1 rounded">
                    <input
                      type="checkbox"
                      checked={(userForm.business_ids || []).includes(biz.id)}
                      onChange={(e) => {
                        setUserForm((prev) => ({
                          ...prev,
                          business_ids: e.target.checked
                            ? [...(prev.business_ids || []), biz.id]
                            : (prev.business_ids || []).filter(id => id !== biz.id),
                        }));
                      }}
                      data-testid={`new-user-biz-${biz.id}`}
                    />
                    <span>{biz.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-[var(--text-muted)] mt-1 italic">
                {(userForm.business_ids || []).length === 0
                  ? 'Si no eliges ninguno, el usuario se crea sin permisos por negocio (los asignas después).'
                  : `Los permisos del rol se replicarán en ${userForm.business_ids.length} negocio(s). Podrás ajustar después.`}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsUserDialogOpen(false);
                  setUserForm({ name: '', email: '', username: '', password: '', role: 'user', business_ids: [] });
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-user-btn" disabled={creatingUser} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold">
                {creatingUser ? 'Creando…' : 'Crear Usuario'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* MANAGE PERMISSIONS DIALOG */}
      <ConfigurarAcceso user={accessUser} businesses={businesses} open={!!accessUser}
        onClose={() => setAccessUser(null)} onSaved={fetchUsers} allUsers={users} />

      <Dialog open={isPermissionDialogOpen} onOpenChange={(open) => {
        setIsPermissionDialogOpen(open);
        if (!open) {
          setPermissionForm({ business_id: '', permissions: [], tab_permissions: {} });
          setSelectedUser(null);
        }
      }}>
        <DialogContent className="sm:max-w-[500px] h-[88vh] sm:h-auto sm:max-h-[88vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-[var(--border)]">
            <DialogTitle>
              Gestionar Permisos - {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.assigned_permissions?.length > 0
                ? `${selectedUser.assigned_permissions.length} negocio(s) asignado(s)`
                : 'Sin permisos asignados'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">

          {selectedUser?.assigned_permissions?.length > 0 && (
            <div className="bg-[var(--bg)] border border-[var(--border)] rounded-[12px] p-3 mb-3 max-h-[30vh] overflow-y-auto">
              <p className="text-xs font-bold text-[var(--text-muted)] mb-2 uppercase tracking-wider sticky top-0 bg-[var(--bg)] pb-1">Permisos actuales</p>
              {selectedUser.assigned_permissions.map((ap, idx) => (
                <div key={idx} className="mb-1.5 last:mb-0">
                  <span className="text-xs font-bold text-[var(--text)]">{ap.business_name}: </span>
                  <span className="text-xs text-[var(--text-muted)]">{ap.permissions.join(', ')}</span>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleUpdatePermissions} className="space-y-4" data-testid="permissions-form">
            <div>
              <Label htmlFor="business">Negocio</Label>
              <select
                id="business"
                value={permissionForm.business_id}
                onChange={(e) => {
                  const bizId = e.target.value;
                  const existingPerm = selectedUser?.assigned_permissions?.find(
                    (ap) => ap.business_id === bizId
                  );
                  setPermissionForm({
                    business_id: bizId,
                    permissions: existingPerm ? [...existingPerm.permissions] : [],
                    tab_permissions: existingPerm?.tab_permissions ? { ...existingPerm.tab_permissions } : {},
                  });
                }}
                required
                className="w-full text-sm p-2 border border-[var(--border)] rounded-[12px] focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none"
                data-testid="permission-business-select"
              >
                <option value="">Seleccionar negocio</option>
                {businesses.map((business) => {
                  const hasPerms = selectedUser?.assigned_permissions?.some(
                    (ap) => ap.business_id === business.id
                  );
                  return (
                    <option key={business.id} value={business.id}>
                      {business.name}{hasPerms ? ' (con permisos)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Módulos Permitidos</Label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      const tabPerms = {};
                      availablePermissions.forEach((m) => {
                        if (MODULE_TABS[m]) tabPerms[m] = MODULE_TABS[m].map((t) => t.key);
                      });
                      setPermissionForm(prev => ({ ...prev, permissions: [...availablePermissions], tab_permissions: tabPerms }));
                    }}
                    className="text-[10px] uppercase tracking-wider font-bold text-[#1D4ED8] hover:underline px-2 py-1"
                    data-testid="permission-select-all"
                  >
                    Todos
                  </button>
                  <button
                    type="button"
                    onClick={() => setPermissionForm(prev => ({ ...prev, permissions: [], tab_permissions: {} }))}
                    className="text-[10px] uppercase tracking-wider font-bold text-[#DC2626] hover:underline px-2 py-1"
                    data-testid="permission-select-none"
                  >
                    Ninguno
                  </button>
                </div>
              </div>

              {/* Role presets — usa roles dinámicos de la BD (incluye personalizados) */}
              <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-[var(--border)]">
                <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)] self-center mr-1">Plantilla:</span>
                {(roles.length > 0 ? roles : Object.keys(ROLE_PRESETS).map(k => ({ key: k, label: k }))).filter(r => r.key !== 'admin').map((role) => (
                  <button
                    key={role.key}
                    type="button"
                    onClick={() => applyRolePreset(role.key)}
                    className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-[8px] bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-all"
                    data-testid={`permission-preset-${role.key}`}
                    title={role.description || ''}
                  >
                    {role.label || role.key}
                  </button>
                ))}
              </div>

              {/* Copy permissions from another user (same business) */}
              {permissionForm.business_id && (() => {
                const candidates = users
                  .filter(u => u.id !== selectedUser?.id && u.assigned_permissions?.some(ap => ap.business_id === permissionForm.business_id));
                return (
                  <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-[var(--border)]">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)]">Copiar de:</span>
                    <select
                      onChange={(e) => {
                        const srcId = e.target.value;
                        if (!srcId) return;
                        const src = users.find(u => u.id === srcId);
                        const srcPerm = src?.assigned_permissions?.find(ap => ap.business_id === permissionForm.business_id);
                        if (!srcPerm) {
                          toast.error('Ese usuario no tiene permisos en este negocio');
                          e.target.value = '';
                          return;
                        }
                        setPermissionForm(prev => ({
                          ...prev,
                          permissions: [...(srcPerm.permissions || [])],
                          tab_permissions: srcPerm.tab_permissions ? { ...srcPerm.tab_permissions } : {},
                        }));
                        toast.success(`Permisos copiados de ${src.name} (no olvides guardar)`);
                        e.target.value = '';
                      }}
                      defaultValue=""
                      className="flex-1 min-w-[180px] text-xs px-2 py-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-[8px]"
                      data-testid="copy-from-user-select"
                    >
                      <option value="">— Elige usuario plantilla —</option>
                      {candidates.length === 0 ? (
                        <option value="" disabled>Sin otros usuarios con permisos en este negocio</option>
                      ) : candidates.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.role || 'user'})
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}

              {/* Grouped permission checkboxes */}
              {(() => {
                const groups = [
                  { title: 'Operación diaria', items: ['dashboard', 'mi_dia', 'inbox', 'inbox_asignado', 'pendientes', 'chat_interno'] },
                  { title: 'Ventas & Clientes', items: ['customers', 'orders', 'catalogo', 'templates', 'cotizador'] },
                  { title: 'Producción / Operaciones', items: ['produccion', 'programacion'] },
                  { title: 'Directorio Marketing', items: ['clientes_vendedores', 'clientes_redes'] },
                  { title: 'Reportes & IA', items: ['reports', 'reportes', 'prospectos', 'ai_costos'] },
                  { title: 'Inteligencia (Admin/Gerente)', items: ['alertas', 'wa_campaigns', 'asistente_ia', 'chat_ia', 'dashboard_gerencial', 'dashboard_comercial'] },
                  { title: 'Supervisión', items: ['ranking', 'vendors', 'coverage', 'plan_trabajo'] },
                  { title: 'Sistema', items: ['settings', 'harperp', 'tienda_admin', 'landing_pages', 'leads_landing'] },
                ];
                return (
                  <div className="space-y-3 pr-1">
                    {groups.map((g) => (
                      <div key={g.title}>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-muted)] mb-1.5">{g.title}</p>
                        <div className="grid grid-cols-2 gap-1">
                          {g.items.map((perm) => (
                            <label
                              key={perm}
                              className={`flex items-center gap-2 p-2 rounded-[8px] cursor-pointer border transition-all ${
                                permissionForm.permissions.includes(perm)
                                  ? 'border-[#1D4ED8] bg-[#1D4ED8]/5'
                                  : 'border-[var(--border)] hover:bg-[var(--bg)]'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={permissionForm.permissions.includes(perm)}
                                onChange={() => togglePermission(perm)}
                                className="w-4 h-4 accent-[#1D4ED8]"
                                data-testid={`permission-checkbox-${perm}`}
                              />
                              <span className="text-xs font-bold text-[var(--text)]">{permissionLabels[perm] || perm}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Sub-permissions per module (tabs) */}
                    {Object.keys(MODULE_TABS).map((module) => {
                      if (!permissionForm.permissions.includes(module)) return null;
                      const tabs = MODULE_TABS[module];
                      // Use effective permissions: undefined → all granted (matches backend convention)
                      const selected = getEffectiveTabPermissions(module);
                      const isImplicit = permissionForm.tab_permissions?.[module] === undefined
                        || permissionForm.tab_permissions?.[module] === null;
                      return (
                        <div
                          key={`sub-${module}`}
                          className="mt-3 p-3 rounded-[12px] border-2 border-[#1D4ED8]/40 bg-[#1D4ED8]/5"
                          data-testid={`tab-permissions-${module}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-[#1D4ED8]">
                              ↳ Pestañas de &quot;{permissionLabels[module]}&quot;
                            </p>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => setAllTabPermissions(module, true)}
                                className="text-[10px] uppercase tracking-wider font-bold text-[#1D4ED8] hover:underline px-1.5 py-0.5"
                              >
                                Todas
                              </button>
                              <button
                                type="button"
                                onClick={() => setAllTabPermissions(module, false)}
                                className="text-[10px] uppercase tracking-wider font-bold text-[#DC2626] hover:underline px-1.5 py-0.5"
                              >
                                Ninguna
                              </button>
                            </div>
                          </div>
                          <p className="text-[10px] text-[var(--text-muted)] mb-1.5 italic">
                            {isImplicit
                              ? 'Todas habilitadas (sin restricción). Desmarca las pestañas que NO quieres que vea este usuario.'
                              : 'Desmarca las pestañas que NO quieres que vea este usuario.'}
                          </p>
                          <div className="grid grid-cols-2 gap-1">
                            {tabs.map((tab) => (
                              <label
                                key={tab.key}
                                className={`flex items-center gap-2 p-1.5 rounded-[6px] cursor-pointer border text-xs transition-all ${
                                  selected.includes(tab.key)
                                    ? 'border-[#1D4ED8] bg-[var(--surface)]'
                                    : 'border-[var(--border)] bg-[var(--bg)] opacity-60'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected.includes(tab.key)}
                                  onChange={() => toggleTabPermission(module, tab.key)}
                                  className="w-3.5 h-3.5 accent-[#1D4ED8]"
                                  data-testid={`tab-permission-${module}-${tab.key}`}
                                />
                                <span className="font-bold text-[var(--text)]">{tab.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </form>
          </div>

          <div className="flex justify-between items-center gap-2 px-6 py-4 border-t border-[var(--border)] bg-[var(--surface)] shrink-0 sticky bottom-0 z-10">
              <p className="text-[10px] text-[var(--text-muted)] hidden sm:block">
                {permissionForm.permissions.length} módulo(s) seleccionado(s)
              </p>
              <div className="flex gap-2 ml-auto">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsPermissionDialogOpen(false);
                  setPermissionForm({ business_id: '', permissions: [], tab_permissions: {} });
                  setSelectedUser(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleUpdatePermissions}
                data-testid="save-permissions-btn"
                className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold"
              >
                Guardar Permisos
              </Button>
              </div>
            </div>
        </DialogContent>
      </Dialog>

      {/* ASSIGN CONVERSATION DIALOG */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Asignar Conversación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="assign-conversation-form">
            <p className="text-sm text-[var(--text-muted)]">
              Selecciona el usuario al que deseas asignar esta conversación
            </p>

            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleAssignConversation(null)}
                data-testid="unassign-conversation-btn"
              >
                Sin asignar
              </Button>

              {users
                .filter((u) => u.role !== 'admin')
                .map((user) => (
                  <Button
                    key={user.id}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleAssignConversation(user.id)}
                    data-testid={`assign-to-${user.id}`}
                  >
                    {user.name} ({user.email || user.username})
                  </Button>
                ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT USER DIALOG */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={(open) => {
        setIsPasswordDialogOpen(open);
        if (!open) { setPasswordEditUser(null); setNewPassword(''); setEditName(''); }
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Editar Usuario - {passwordEditUser?.name}</DialogTitle>
            <DialogDescription>
              {passwordEditUser?.plain_password
                ? `Contrasena actual: ${passwordEditUser.plain_password}`
                : 'Contrasena actual no disponible'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
            <div>
              <Label htmlFor="edit-name">Nombre</Label>
              <Input
                id="edit-name"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Nombre del usuario"
                data-testid="edit-user-name-input"
              />
            </div>
            <div>
              <Label htmlFor="new-password">Nueva Contrasena (dejar vacio para no cambiar)</Label>
              <Input
                id="new-password"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={6}
                placeholder="Nueva contrasena"
                data-testid="new-password-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsPasswordDialogOpen(false);
                setPasswordEditUser(null);
                setNewPassword('');
                setEditName('');
              }}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-password-btn">
                Guardar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Departments Dialog */}
      <Dialog open={isDeptDialogOpen} onOpenChange={setIsDeptDialogOpen}>
        <DialogContent className="max-w-2xl bg-[var(--bg)] border-[var(--border)] text-[var(--text)] max-h-[90vh] overflow-y-auto" data-testid="departments-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--text)] flex items-center gap-2">
              <MapPin size={20} weight="bold" className="text-[#1D4ED8]" />
              Departamentos de {deptUser?.name}
            </DialogTitle>
            <DialogDescription className="text-[var(--text-muted)]">
              Selecciona los departamentos que este vendedor atenderá. Si varios vendedores comparten un departamento, las conversaciones se distribuirán por rotación (round-robin).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-bold text-[var(--text-muted)] uppercase mb-2 tracking-wider">Guatemala</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {deptList.guatemala.map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDept(d)}
                    className={`text-xs px-3 py-2 rounded-[8px] font-bold border transition-all text-left ${
                      selectedDepts.includes(d)
                        ? 'bg-[#1D4ED8] border-[#1D4ED8] text-white shadow-[0_0_10px_rgba(29,78,216,0.4)]'
                        : 'bg-[var(--surface)] border-[var(--border)] text-[var(--text)] hover:border-[#1D4ED8]/50'
                    }`}
                    data-testid={`dept-toggle-${d}`}
                  >
                    {selectedDepts.includes(d) ? '✓ ' : ''}{d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--text-muted)] uppercase mb-2 tracking-wider">Otros</p>
              <div className="grid grid-cols-2 gap-2">
                {deptList.special.map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDept(d)}
                    className={`text-xs px-3 py-2 rounded-[8px] font-bold border transition-all text-left ${
                      selectedDepts.includes(d)
                        ? 'bg-[#7C3AED] border-[#7C3AED] text-white shadow-[0_0_10px_rgba(124,58,237,0.4)]'
                        : 'bg-[var(--surface)] border-[var(--border)] text-[var(--text)] hover:border-[#7C3AED]/50'
                    }`}
                    data-testid={`dept-toggle-special-${d}`}
                  >
                    {selectedDepts.includes(d) ? '✓ ' : ''}{d}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-[var(--border)]">
              <span className="text-xs text-[var(--text-muted)] font-bold">{selectedDepts.length} departamento{selectedDepts.length !== 1 ? 's' : ''} seleccionado{selectedDepts.length !== 1 ? 's' : ''}</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsDeptDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleSaveDepartments} data-testid="save-departments-btn" className="bg-[#1D4ED8] hover:bg-[#1E40AF]">Guardar</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Módulos bloqueados (denylist) */}
      <Dialog open={isDenyDialogOpen} onOpenChange={(o) => { if (!o) { setIsDenyDialogOpen(false); setDenyTargetUser(null); } }}>
        <DialogContent className="max-w-lg bg-[var(--surface)] border-[var(--border)]" data-testid="deny-modules-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--text)] flex items-center gap-2">
              <ShieldSlash size={20} weight="duotone" className="text-red-600" />
              Módulos bloqueados — {denyTargetUser?.name}
            </DialogTitle>
            <DialogDescription className="text-[var(--text-muted)] text-xs">
              Marca los módulos a los que este usuario NO debe tener acceso. Aplica globalmente (en cualquier negocio).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-3">
            {DENYABLE_MODULES.map((m) => {
              const checked = denyDraft.includes(m.id);
              return (
                <label
                  key={m.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    checked
                      ? 'border-red-300 bg-red-50 dark:bg-red-900/20'
                      : 'border-[var(--border)] bg-[var(--bg)] hover:border-[var(--text-muted)]'
                  }`}
                  data-testid={`deny-row-${m.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setDenyDraft((arr) => checked ? arr.filter(x => x !== m.id) : [...arr, m.id])}
                    className="mt-0.5 w-4 h-4 accent-red-600"
                    data-testid={`deny-check-${m.id}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-[var(--text)]">{m.label}</div>
                    <div className="text-[11px] text-[var(--text-muted)]">{m.desc}</div>
                  </div>
                  {checked && (
                    <span className="text-[10px] font-black px-2 py-0.5 rounded bg-red-600 text-white">BLOQUEADO</span>
                  )}
                </label>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button variant="outline" onClick={() => setIsDenyDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSaveDenyModules}
              data-testid="save-deny-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Guardar bloqueos ({denyDraft.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Create/Edit Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="sm:max-w-[640px] h-[88vh] sm:h-auto sm:max-h-[88vh] flex flex-col p-0 gap-0 overflow-hidden" data-testid="role-dialog">
          <DialogHeader className="px-6 py-4 border-b border-[var(--border)] shrink-0">
            <DialogTitle>{editingRole ? `Editar rol: ${editingRole.label}` : 'Crear nuevo rol'}</DialogTitle>
            <DialogDescription>
              {editingRole
                ? 'Modifica los permisos que se aplicarán por defecto a los nuevos usuarios con este rol.'
                : 'Define los permisos que se aplicarán automáticamente al crear usuarios con este rol.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
            {!editingRole && (
              <div>
                <Label htmlFor="role-key" className="text-xs">Clave interna (ej: jefe_taller — sin espacios, snake_case)</Label>
                <Input
                  id="role-key"
                  value={roleForm.key}
                  onChange={(e) => setRoleForm({ ...roleForm, key: e.target.value })}
                  placeholder="jefe_taller"
                  className="text-sm font-mono"
                  data-testid="role-key-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="role-label" className="text-xs">Nombre visible</Label>
              <Input
                id="role-label"
                value={roleForm.label}
                onChange={(e) => setRoleForm({ ...roleForm, label: e.target.value })}
                placeholder="Jefe de Taller"
                className="text-sm"
                data-testid="role-label-input"
              />
            </div>
            <div>
              <Label htmlFor="role-desc" className="text-xs">Descripción (opcional)</Label>
              <Input
                id="role-desc"
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                placeholder="Coordina materiales y mano de obra en producción"
                className="text-sm"
              />
            </div>
            <div className="border-t border-[var(--border)] pt-3">
              <p className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)] mb-2">
                Permisos por defecto ({roleForm.permissions.length} seleccionados)
              </p>
              {(() => {
                const groups = [
                  { title: 'Operación diaria', items: ['dashboard', 'mi_dia', 'inbox', 'inbox_asignado', 'pendientes', 'chat_interno'] },
                  { title: 'Ventas & Clientes', items: ['customers', 'orders', 'catalogo', 'templates', 'cotizador'] },
                  { title: 'Producción / Operaciones', items: ['produccion', 'programacion'] },
                  { title: 'Directorio Marketing', items: ['clientes_vendedores', 'clientes_redes'] },
                  { title: 'Reportes & IA', items: ['reports', 'reportes', 'prospectos', 'ai_costos'] },
                  { title: 'Inteligencia (Admin/Gerente)', items: ['alertas', 'wa_campaigns', 'asistente_ia', 'chat_ia', 'dashboard_gerencial', 'dashboard_comercial'] },
                  { title: 'Supervisión', items: ['ranking', 'vendors', 'coverage', 'plan_trabajo'] },
                  { title: 'Sistema', items: ['settings', 'harperp', 'tienda_admin', 'landing_pages', 'leads_landing'] },
                ];
                return groups.map((g) => (
                  <div key={g.title} className="mb-3">
                    <p className="text-[10px] font-bold uppercase text-[var(--text-muted)] mb-1.5">{g.title}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {g.items.map((p) => (
                        <label key={p} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[var(--surface-muted)] px-2 py-1 rounded">
                          <input
                            type="checkbox"
                            checked={roleForm.permissions.includes(p)}
                            onChange={() => toggleRolePermission(p)}
                            data-testid={`role-perm-${p}`}
                          />
                          <span>{permissionLabels[p] || p}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border)] bg-[var(--surface)] shrink-0 sticky bottom-0 z-10">
            <Button variant="outline" onClick={() => setIsRoleDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSaveRole}
              disabled={savingRole}
              className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold"
              data-testid="save-role-btn"
            >
              {savingRole ? 'Guardando…' : (editingRole ? 'Guardar Cambios' : 'Crear Rol')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
